import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Shield, CheckSquare, Square } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { staffService } from '../../services/staffService';
import { customToast } from '../../utils/customToast';
import { StaffMember, AvailableModule } from '../../types';

interface StaffPermissionsModalProps {
    isOpen: boolean;
    staff: StaffMember | null;
    onClose: () => void;
    onSuccess: () => void;
}

const StaffPermissionsModal: React.FC<StaffPermissionsModalProps> = ({ isOpen, staff, onClose, onSuccess }) => {
    const { theme } = useTheme();
    const [loading, setLoading] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [availableModules, setAvailableModules] = useState<AvailableModule[]>([]);
    const [loadingModules, setLoadingModules] = useState(true);

    const [permissions, setPermissions] = useState<Record<string, {
        can_view: boolean;
        can_create: boolean;
        can_edit: boolean;
        can_delete: boolean;
    }>>({});

    useEffect(() => {
        if (isOpen && staff) {
            loadModules();
        }
    }, [isOpen, staff]);

    const loadModules = async () => {
        if (!staff) return;

        try {
            setLoadingModules(true);
            const modules = await staffService.getAvailableModules();
            setAvailableModules(modules);

            // Initialize permissions from staff's current permissions
            const initialPermissions: Record<string, any> = {};
            modules.forEach(mod => {
                const existing = staff.permissions?.find(p => p.module === mod.key);
                initialPermissions[mod.key] = {
                    can_view: existing?.can_view || false,
                    can_create: existing?.can_create || false,
                    can_edit: existing?.can_edit || false,
                    can_delete: existing?.can_delete || false,
                };
            });
            setPermissions(initialPermissions);
        } catch (error) {
            console.error('Failed to load modules:', error);
            customToast.error('Failed to load permission modules');
        } finally {
            setLoadingModules(false);
        }
    };

    if (!isOpen || !staff) return null;

    const handlePermissionChange = (module: string, permission: string, value: boolean) => {
        setPermissions(prev => ({
            ...prev,
            [module]: {
                ...prev[module],
                [permission]: value,
                // If enabling create/edit/delete, also enable view
                ...(permission !== 'can_view' && value ? { can_view: true } : {}),
                // If disabling view, disable all others
                ...(permission === 'can_view' && !value ? { can_create: false, can_edit: false, can_delete: false } : {}),
            },
        }));
    };

    const handleSelectAllForModule = (module: string, allSelected: boolean) => {
        setPermissions(prev => ({
            ...prev,
            [module]: {
                can_view: !allSelected,
                can_create: !allSelected,
                can_edit: !allSelected,
                can_delete: !allSelected,
            },
        }));
    };

    const handleSelectAll = () => {
        const allSelected = Object.values(permissions).every(p =>
            p.can_view && p.can_create && p.can_edit && p.can_delete
        );

        const newPermissions: Record<string, any> = {};
        availableModules.forEach(mod => {
            newPermissions[mod.key] = {
                can_view: !allSelected,
                can_create: !allSelected,
                can_edit: !allSelected,
                can_delete: !allSelected,
            };
        });
        setPermissions(newPermissions);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            setLoading(true);

            const permissionsArray = Object.entries(permissions)
                .map(([module, p]) => ({
                    module,
                    can_view: p.can_view,
                    can_create: p.can_create,
                    can_edit: p.can_edit,
                    can_delete: p.can_delete,
                }));

            await staffService.updatePermissions(staff.id, permissionsArray);
            customToast.success('Permissions updated successfully');
            onSuccess();
            handleClose();
        } catch (error: any) {
            console.error('Error updating permissions:', error);
            customToast.error(error.response?.data?.message || 'Failed to update permissions');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            setIsClosing(false);
            setPermissions({});
            onClose();
        }, 150);
    };

    const allSelected = Object.values(permissions).every(p =>
        p.can_view && p.can_create && p.can_edit && p.can_delete
    );

    return createPortal(
        <div className={`fixed inset-0 z-50 overflow-y-auto ${isClosing ? 'animate-fadeOut' : 'animate-fadeIn'}`}>
            <div className="flex items-center justify-center min-h-screen p-4">
                <div
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
                    onClick={handleClose}
                />

                <div className={`relative w-full max-w-2xl overflow-hidden text-left transform shadow-2xl rounded-lg ${isClosing ? 'animate-scaleOut' : 'animate-scaleIn'
                    } ${theme === 'dark' ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}>
                    {/* Header */}
                    <div className={`px-4 sm:px-6 py-4 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                        }`}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-indigo-900/50' : 'bg-indigo-100'
                                    }`}>
                                    <Shield className={`w-5 h-5 ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600'
                                        }`} />
                                </div>
                                <div>
                                    <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                        Manage Permissions
                                    </h2>
                                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                        {staff.full_name} ({staff.staff_role_type})
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={handleClose}
                                className={`p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-600'
                                    }`}
                            >
                                <X size={20} />
                            </button>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="px-4 sm:px-6 py-4 max-h-[calc(100vh-16rem)] sm:max-h-[60vh] overflow-y-auto">
                            {loadingModules ? (
                                <div className={`border rounded-lg overflow-hidden ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                                    <div className={`${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'} p-4`}>
                                        <div className="flex items-center space-x-3 mb-4">
                                            <div className={`h-8 w-32 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'}`} />
                                            <div className={`h-8 w-16 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'}`} />
                                            <div className={`h-8 w-16 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'}`} />
                                            <div className={`h-8 w-16 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'}`} />
                                            <div className={`h-8 w-16 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'}`} />
                                        </div>
                                        <div className="space-y-2">
                                            {[...Array(5)].map((_, idx) => (
                                                <div key={idx} className="flex items-center space-x-3">
                                                    <div className={`h-6 w-32 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'}`} />
                                                    <div className={`h-6 w-12 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'}`} />
                                                    <div className={`h-6 w-12 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'}`} />
                                                    <div className={`h-6 w-12 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'}`} />
                                                    <div className={`h-6 w-12 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'}`} />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {/* Quick Actions */}
                                    <div className="mb-4 flex items-center justify-end">
                                        <button
                                            type="button"
                                            onClick={handleSelectAll}
                                            className="flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors touch-manipulation"
                                        >
                                            {allSelected ? (
                                                <CheckSquare size={18} className="text-indigo-600 dark:text-indigo-400" />
                                            ) : (
                                                <Square size={18} className={theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} />
                                            )}
                                            <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                                                {allSelected ? 'Deselect All' : 'Select All'}
                                            </span>
                                        </button>
                                    </div>

                                    {/* Permissions Table */}
                                    <div className={`border rounded-lg overflow-hidden ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                                        <table className="w-full">
                                            <thead className={theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}>
                                                <tr>
                                                    <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>
                                                        Module
                                                    </th>
                                                    <th className={`px-2 py-3 text-center text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>
                                                        View
                                                    </th>
                                                    <th className={`px-2 py-3 text-center text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>
                                                        Create
                                                    </th>
                                                    <th className={`px-2 py-3 text-center text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>
                                                        Edit
                                                    </th>
                                                    <th className={`px-2 py-3 text-center text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>
                                                        Delete
                                                    </th>
                                                    <th className={`px-2 py-3 text-center text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>
                                                        All
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
                                                {availableModules.map((mod) => {
                                                    const perm = permissions[mod.key] || { can_view: false, can_create: false, can_edit: false, can_delete: false };
                                                    const moduleAllSelected = perm.can_view && perm.can_create && perm.can_edit && perm.can_delete;

                                                    return (
                                                        <tr key={mod.key} className={theme === 'dark' ? 'hover:bg-gray-750' : 'hover:bg-gray-50'}>
                                                            <td className={`px-4 py-3 text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                                                                {mod.name}
                                                            </td>
                                                            <td className="px-2 py-3 text-center">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handlePermissionChange(mod.key, 'can_view', !perm.can_view)}
                                                                    className="inline-flex items-center justify-center p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                                                                    title="View permission"
                                                                >
                                                                    {perm.can_view ? (
                                                                        <CheckSquare size={20} className="text-blue-600 dark:text-blue-400" />
                                                                    ) : (
                                                                        <Square size={20} className={theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} />
                                                                    )}
                                                                </button>
                                                            </td>
                                                            <td className="px-2 py-3 text-center">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handlePermissionChange(mod.key, 'can_create', !perm.can_create)}
                                                                    className="inline-flex items-center justify-center p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                                                                    title="Create permission"
                                                                >
                                                                    {perm.can_create ? (
                                                                        <CheckSquare size={20} className="text-green-600 dark:text-green-400" />
                                                                    ) : (
                                                                        <Square size={20} className={theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} />
                                                                    )}
                                                                </button>
                                                            </td>
                                                            <td className="px-2 py-3 text-center">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handlePermissionChange(mod.key, 'can_edit', !perm.can_edit)}
                                                                    className="inline-flex items-center justify-center p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                                                                    title="Edit permission"
                                                                >
                                                                    {perm.can_edit ? (
                                                                        <CheckSquare size={20} className="text-amber-600 dark:text-amber-400" />
                                                                    ) : (
                                                                        <Square size={20} className={theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} />
                                                                    )}
                                                                </button>
                                                            </td>
                                                            <td className="px-2 py-3 text-center">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handlePermissionChange(mod.key, 'can_delete', !perm.can_delete)}
                                                                    className="inline-flex items-center justify-center p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                                                                    title="Delete permission"
                                                                >
                                                                    {perm.can_delete ? (
                                                                        <CheckSquare size={20} className="text-red-600 dark:text-red-400" />
                                                                    ) : (
                                                                        <Square size={20} className={theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} />
                                                                    )}
                                                                </button>
                                                            </td>
                                                            <td className="px-2 py-3 text-center">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleSelectAllForModule(mod.key, moduleAllSelected)}
                                                                    className="inline-flex items-center justify-center p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                                                                    title="Select all permissions for this module"
                                                                >
                                                                    {moduleAllSelected ? (
                                                                        <CheckSquare size={20} className="text-indigo-600 dark:text-indigo-400" />
                                                                    ) : (
                                                                        <Square size={20} className={theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} />
                                                                    )}
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>

                                    <p className={`mt-3 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                        Note: Enabling Create, Edit, or Delete will automatically enable View for that module.
                                    </p>
                                </>
                            )}
                        </div>

                        {/* Footer */}
                        <div className={`px-4 sm:px-6 py-4 border-t flex flex-col-reverse sm:flex-row space-y-reverse space-y-2 sm:space-y-0 sm:space-x-3 ${theme === 'dark' ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'
                            }`}>
                            <button
                                type="button"
                                onClick={handleClose}
                                disabled={loading}
                                className={`w-full sm:flex-1 px-4 py-2.5 sm:py-2 text-sm font-medium rounded-md border transition-colors ${theme === 'dark'
                                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading || loadingModules}
                                className="w-full sm:flex-1 px-4 py-2.5 sm:py-2 text-sm font-medium rounded-md transition-colors flex items-center justify-center space-x-2 bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? (
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                ) : null}
                                <span>{loading ? 'Saving...' : 'Save Permissions'}</span>
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default StaffPermissionsModal;
